/**
 * V12 数据中心 — 统一数据代理
 *
 * 所有业务模块的数据读写经由此层。DataProxy 根据模块元数据自动确定：
 * - 键名格式（账号: `{prefix}-{module}-{openid}`, device: `{prefix}-{module}`）
 * - 账号模块未登录时只读为空、写入抛出 AUTH_REQUIRED，不创建匿名业务数据
 * - 存储后端（当前只支持 localStorage）
 *
 * 设计原则：
 * - 不改变现有模块的存储格式，仅改变 key 命名规则
 * - 所有数据操作都是同步的（localStorage 天然同步），对外提供 Promise 包装便于未来迁移
 * - DEVICE_KEYS（llm-api-key 等）不经过 DataProxy，由原模块直读 localStorage
 *
 * @see design/v12-user-data-center/spec.md §FR-001/FR-002
 */

import { AuthRequiredError, getCurrentUserOpenid, isLoggedIn } from "./user-context";
import {
  type DataModule,
  DATA_MODULES,
  MODULE_REGISTRY,
  DEVICE_KEYS,
} from "./data-registry";

// ============================================================
// 常量
// ============================================================

const KEY_PREFIX = "battlecode";
const DEVICE_KEYS_SET = new Set<string>(DEVICE_KEYS);

// ============================================================
// 键名构造
// ============================================================

/**
 * 构造模块的数据键名。
 * - user scope: `battlecode-{module}-{openid}`
 * - device scope: `battlecode-{module}`
 */
function buildKey(module: DataModule): string {
  const meta = MODULE_REGISTRY[module];
  if (meta.scope === "device") {
    return `${KEY_PREFIX}-${module}`;
  }
  const openid = getCurrentUserOpenid();
  if (!openid || !isLoggedIn()) throw new AuthRequiredError();
  return `${KEY_PREFIX}-${module}-${encodeURIComponent(openid)}`;
}



// ============================================================
// DataProxy 类
// ============================================================

class DataProxy {
  // ========== 读操作 ==========

  /** 读取模块数据 */
  get<T = unknown>(module: DataModule): T | null {
    if (MODULE_REGISTRY[module].scope === "user" && !isLoggedIn()) return null;
    // 账号已登录时读取账号命名空间
    const key = buildKey(module);
    const raw = localStorage.getItem(key);
    if (raw !== null) {
      try {
        return JSON.parse(raw) as T;
      } catch {
        console.warn(`[DataProxy] JSON parse failed for ${key}, returning null`);
        return null;
      }
    }
    return null;
  }

  /** 读取模块原始字符串（不做 JSON parse） */
  getRaw(module: DataModule): string | null {
    if (MODULE_REGISTRY[module].scope === "user" && !isLoggedIn()) return null;
    const key = buildKey(module);
    return localStorage.getItem(key);
  }

  /** 检查模块是否有数据 */
  has(module: DataModule): boolean {
    if (MODULE_REGISTRY[module].scope === "user" && !getCurrentUserOpenid()) return false;
    return localStorage.getItem(buildKey(module)) !== null;
  }

  // ========== 写操作 ==========

  /** 写入模块数据 */
  set<T = unknown>(module: DataModule, data: T): void {
    if (MODULE_REGISTRY[module].scope === "user" && !isLoggedIn()) return;
    const key = buildKey(module);
    localStorage.setItem(key, JSON.stringify(data));
  }

  /** 写入原始字符串 */
  setRaw(module: DataModule, data: string): void {
    if (MODULE_REGISTRY[module].scope === "user" && !isLoggedIn()) return;
    const key = buildKey(module);
    localStorage.setItem(key, data);
  }

  // ========== 删除操作 ==========

  /** 删除模块数据 */
  remove(module: DataModule): void {
    if (MODULE_REGISTRY[module].scope === "user" && !getCurrentUserOpenid()) return;
    localStorage.removeItem(buildKey(module));
  }

  // ========== 迁移操作 ==========

  /**
   * 检测是否有遗留数据需要迁移。
   * 条件：当前已登录 + 旧 key 有数据 + 新 key 无数据
   */
  needsMigration(): boolean {
    // V14 账号唯一模式：匿名数据永久放弃，不迁移到任何账号。
    return false;
  }

  /**
   * 获取可迁移的模块列表
   */
  getMigratableModules(): DataModule[] {
    // V14 账号唯一模式：不暴露匿名数据迁移入口。
    return [];
  }

  /**
   * 执行数据迁移：将旧 key 数据复制到新 key，并删除旧 key。
   * 返回迁移成功的模块列表。
   */
  migrate(): DataModule[] {
    // V14 账号唯一模式：匿名数据不迁移；启动清理器负责删除旧 key。
    console.info("[DataProxy] anonymous migration disabled");
    return [];
  }

  /** 删除当前账号的数据；未登录时不删除匿名设备数据。 */
  removeAllUserData(): void {
    const openid = getCurrentUserOpenid();
    if (!openid || !isLoggedIn()) {
      console.info("[DataProxy] removeAllUserData skipped: no logged-in user");
      return;
    }

    const suffix = `-${encodeURIComponent(openid)}`;
    const moduleKeys = new Set(DATA_MODULES);
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(`${KEY_PREFIX}-`) || !key.endsWith(suffix)) continue;
      const moduleName = key.slice(`${KEY_PREFIX}-`.length, -suffix.length);
      if (moduleKeys.has(moduleName as DataModule)) keysToRemove.push(key);
    }

    for (const key of keysToRemove) localStorage.removeItem(key);
    console.info(`[DataProxy] Removed ${keysToRemove.length} data keys for current user`);
  }

  // ========== 设备 key 检查（不经 DataProxy） ==========

  /** 判断一个 localStorage key 是否为设备级 key */
  isDeviceKey(key: string): boolean {
    return DEVICE_KEYS_SET.has(key);
  }
}

// ============================================================
// 写操作回调钩子（供 sync-client 注册）
// ============================================================

type DataWriteHook = (module: DataModule) => void;
let _onDataWrite: DataWriteHook | null = null;

/** 注册写操作回调（sync-client 初始化时调用） */
export function setOnDataWrite(fn: DataWriteHook | null): void {
  _onDataWrite = fn;
}

// ============================================================
// 单例导出
// ============================================================

class DataProxyWithHook extends DataProxy {
  set<T = unknown>(module: DataModule, data: T): void {
    super.set(module, data);
    if (_onDataWrite) _onDataWrite(module);
  }

  remove(module: DataModule): void {
    super.remove(module);
    // 删除不触发同步（本地上传 null 无意义）
  }
}

export const dataProxy = new DataProxyWithHook();
